import { useContext, useMemo, useEffect, useCallback } from "react"
import { useDispatch } from "react-redux"
import PropTypes from "prop-types"
import { size, isEmpty } from "lodash"
import AddTrade from "connected-components/add-trade"
import { Creators as DialogCreators } from "modules/dialog/model"
import AISummarizer from "modules/ai-summary"
import { ACTIONS } from "modules/ai-summary/constants"
import SaveExperiment from "modules/save-experiment"
import ResetButton from "modules/rebalance/components/reset-button"
import { computeAnalyticsOutput } from "modules/rebalance/helpers/queries/helpers"
import SaveExperimentButton from "components/save-experiment-button"
import { useRebalancer, useSimulateActions, computeSimulationExperiment, usePortfolioHoldings } from "hooks/rebalancer"
import { useIsAiTermsAccepted, useAccountAlias } from "resources/account"
import { useModelMetadata } from "resources/models"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import { computeAttributionMethod } from "helpers/risk"
import { computeFilteredSegment } from "helpers/segments"
import { useClassifications } from "resources/classifications"
import { LOADING, OK } from "helpers/data-statuses"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "../../analytics"

export default function TraderManager({ context, instrumentsWithMetadata }) {
  const dispatch = useDispatch()
  const {
    modelId,
    endDate,
    instrument,
    setSecondInstrument,
    trades,
    resetTrades,
    base,
    filter,
    actionsBase,
    expand,
    normalization,
    setComparisonInstrument,
    comparisonInstrument,
    intradayBatchMetadata,
    startDate,
    isIntraday,
    updateTradeFilters,
    selectedMode,
    assetViewGroupBy: currentGrouping } = useContext(context)

  const analytics = useAnalytics()

  const isActive = !!base
  const hasFilters = !isEmpty(filter)

  // Will need to update this to account for intraday when that is implemented
  const attributionMethod = useMemo(() => computeAttributionMethod(isActive), [isActive])
  // TODO: This is a shortcut until we support experiments for other instruments than portfolios
  const [portfolioHoldings, portfolioHoldingsStatus] = usePortfolioHoldings(instrument, modelId, endDate)
  const [secondInstrument, secondInstrumentStatus] = useRebalancer(trades, instrument, modelId, endDate, base, intradayBatchMetadata?.id)
  // Do not handle the status, model metadata is already fetched in the dashboard
  const [modelMetadata] = useModelMetadata(modelId)
  const simulateActions = useSimulateActions(trades)
  const isAiTermsAccepted = useIsAiTermsAccepted()
  const account = useAccountAlias()
  const analyticsSegment = useMemo(() => computeFilteredSegment(filter, normalization), [normalization, filter])
  const aiSummarizerBase = useMemo(() => (base ? { id: base.id, type: base.type, weight: base.weight } : null), [base])
  const { analyticsOutput } = useMemo(() => computeAnalyticsOutput({ isActive, base: aiSummarizerBase, analyticsSegment, expand }), [isActive, aiSummarizerBase, analyticsSegment, expand])
  const [classifications, classificationsStatus] = useClassifications()

  useEffect(() => {
    // Avoid updating second instrument if it's still loading and there are trades
    if (secondInstrumentStatus === LOADING && trades.length > 0) {
      return
    }

    // After the initial trade, keep whatever mode was selected
    const overrideMode = trades.length > 1 ? selectedMode : null
    setSecondInstrument(secondInstrument, secondInstrumentStatus, overrideMode)
  }, [secondInstrument, secondInstrumentStatus, setSecondInstrument, selectedMode, trades])

  useEffect(() => {
    updateTradeFilters(filter)
  }, [filter, updateTradeFilters])

  const handleResetRebalance = () => {
    const nbTrades = size(trades)
    dispatch(DialogCreators.open({
      title: "Reset Trades",
      contentText: `Are you sure you want to reset your ${nbTrades === 1 ? "trade" : `${nbTrades} trades`} ?`,
      buttons: [{
        text: "Reset trades",
        type: "danger",
        action: () => {
          analytics.track(DASHBOARD_EVENTS.TRADES_CLEARED, { numberOfTrades: nbTrades })
          resetTrades()
        },
      }],
    }))
  }

  const computedExperiment = useMemo(() => {
    let experimentDate = secondInstrument?.dates[0]

    if (isEmpty(experimentDate) && portfolioHoldingsStatus === OK) {
      experimentDate = portfolioHoldings
    }

    return computeSimulationExperiment(experimentDate, actionsBase, simulateActions, null, endDate)
  },
  [simulateActions, actionsBase, endDate, secondInstrument, portfolioHoldings, portfolioHoldingsStatus])

  const showSuccessDialog = useCallback((newExperiment) => {
    const newComparisonInstrument = {
      type: instrument.type,
      id: instrument.id,
      experimentId: newExperiment.id,
    }

    dispatch(DialogCreators.open({
      title: "Use as comparison instrument?",
      contentText: `Would you like to use "${newExperiment.name}" as a comparison instrument? ${comparisonInstrument ? " This will replace the current comparison instrument." : ""}`,
      cancelText: "No",
      buttons: [{ text: "Yes", type: "primary", action: () => setComparisonInstrument(newComparisonInstrument) }],
    }))
  }, [dispatch, setComparisonInstrument, instrument, comparisonInstrument])

  const toggleSaveExperiment = useCallback(() => {
    const isOneDayExperiment = endDate === modelMetadata?.availability.currentDate
    dispatch(DialogCreators.open({
      title: "Save Experiment",
      content: <SaveExperiment
        experiment={computedExperiment}
        portfolioId={instrumentsWithMetadata?.original.id}
        portfolioAlias={instrumentsWithMetadata?.original.alias}
        isOneDayExperiment={isOneDayExperiment}
        onSuccess={showSuccessDialog}
        searchType={RECENT_INSTRUMENT_SEARCH_TYPES.DASHBOARD}
      />,
    }))
  }, [dispatch, computedExperiment, instrumentsWithMetadata, modelMetadata, endDate, showSuccessDialog])

  const canSaveExperiment = useMemo(() =>
    instrumentsWithMetadata?.original.type === "PORTFOLIO" && !isEmpty(computedExperiment) && !isIntraday,
  [instrumentsWithMetadata, computedExperiment, isIntraday])

  const tooltipOverride = useMemo(() => {
    if (instrumentsWithMetadata?.original.type !== "PORTFOLIO") {
      return "Can only save experiments on portfolios"
    }

    if (isIntraday) {
      return "Can't save experiments in intraday mode"
    }

    return undefined
  }, [instrumentsWithMetadata, isIntraday])

  const Summarizer = isAiTermsAccepted && instrument?.type === "PORTFOLIO"
    ? <div className="dashboard-ai-summarizer">
      <AISummarizer
        key="aiSummarizer"
        isDashboard={true}
        {...{
          noSpinner: true,
          tooltip: "Open Simulator Summary",
          action: ACTIONS.SIMULATOR_SUMMARY,
          account,
          modelId,
          resourceId: instrument.id,
          resourceType: "PORTFOLIO",
          ...(isActive && { base: aiSummarizerBase }),
          startDate: startDate,
          endDate: endDate,
          extraParameters: {
            attributionMethod,
            actions: simulateActions,
            actionsBase,
            batchId: null,
            analyticsOutput,
          },
        }}
      />
    </div>
    : null

  return (
    <div style={{ display: "flex" }}>
      {/* Classications changing from LOADING to OK or ERROR will trigger a re-render of the AddTrade component which will
      trigger a re-fetch of all the queries. OK and ERROR are both ok as they are stable states. */}
      {classificationsStatus !== LOADING && <AddTrade context={context} classifications={classifications} currentGrouping={currentGrouping} hasFilters={hasFilters} />}
      {Summarizer}
      <ResetButton disabled={!trades.length} resetRebalance={handleResetRebalance} />
      <SaveExperimentButton {...{ toggleSaveExperiment, saveDisabled: !canSaveExperiment, tooltipOverride }} />
    </div>
  )
}

TraderManager.propTypes = {
  context: PropTypes.shape({
    modelId: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    instrument: PropTypes.object.isRequired,
    setSecondInstrument: PropTypes.func.isRequired,
    trades: PropTypes.array.isRequired,
    resetTrades: PropTypes.func.isRequired,
  }).isRequired,
  instrumentsWithMetadata: PropTypes.shape({
    original: PropTypes.object,
    rebalanced: PropTypes.object,
    comparison: PropTypes.object,
    base: PropTypes.object,
  }).isRequired,
}
